import { useEffect, useState } from 'react';
import type { LandingPageContent } from '#/domains/landing/types/content';
import { getRuntimeBase } from '#/utils/runtimeBase';

/**
 * 받아온 JSON을 state에 넣기 전에 스키마로 검증한다 — **개발/프리뷰 빌드에서만**.
 *
 * 왜 개발에서만인가:
 * - 배포되는 JSON은 `yarn validate:landing-content`와 빌드 플러그인이 **같은
 *   스키마로 이미 통과시킨 파일**이다. 운영에서 한 번 더 도는 검사는 새로 알아낼
 *   게 거의 없다.
 * - 대신 zod를 런타임 번들에 넣으면 gzip 기준 20KB가 넘게 늘어난다. 인앱 웹뷰로
 *   열리는 마케팅 페이지에 치를 값이 아니다.
 *
 * 그래서 스키마는 `import.meta.env.DEV` 안에서만 **동적 import**한다 → 운영
 * 번들에서는 이 가지가 통째로 사라진다(zod도 함께).
 */
async function findContentProblem(
  data: unknown,
  jsonPath: string
): Promise<string | null> {
  if (!import.meta.env.DEV) return null;

  const { parseLandingPageContent, formatLandingPageIssues } = await import(
    '#/domains/landing/schema/landingPageContent'
  );
  const result = parseLandingPageContent(data);
  if (result.success) return null;

  const issues = formatLandingPageIssues(result.error);
  return `${jsonPath} 내용이 스키마와 맞지 않아요:\n- ${issues.join('\n- ')}`;
}

/**
 * Loads `landing-page.json` for the current slug (root vs nested persona paths).
 */
export function useLandingPageContent(slugPath: string) {
  const base = getRuntimeBase();
  const effectiveSlugPath =
    slugPath === '' || slugPath === '01' ? '' : slugPath;
  const contentBase = effectiveSlugPath ? `${base}${effectiveSlugPath}/` : base;

  const [content, setContent] = useState<LandingPageContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    const jsonPath = effectiveSlugPath
      ? `${effectiveSlugPath}/landing-page.json`
      : `landing-page.json`;

    // A→B로 이동했는데 A의 응답이 늦게 도착하면, B 화면에 A의 내용이 그려지고
    // PV 이벤트도 엉뚱한 페이지로 찍힌다. 이전 요청은 취소하고, 그래도 늦게
    // 돌아온 응답은 `cancelled` 플래그로 버린다(스키마 검증이 await라 취소
    // 이후에도 then이 이어질 수 있다).
    const controller = new AbortController();
    let cancelled = false;

    fetch(`${base}${jsonPath}`, { signal: controller.signal })
      .then(res => {
        if (!res.ok) throw new Error('Failed to load landing page');
        return res.json();
      })
      .then(async (data: unknown) => {
        const problem = await findContentProblem(data, jsonPath);
        if (cancelled) return;
        if (problem != null) {
          setError(problem);
          setContent(null);
          return;
        }
        setContent(data as LandingPageContent);
        setError(null);
      })
      .catch((err: Error) => {
        if (cancelled || err.name === 'AbortError') return;
        setError(err.message);
        setContent(null);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [base, effectiveSlugPath]);

  return { content, loading, error, contentBase };
}
